import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { useAuth } from "common/context/AuthContext";
import useInvitations from "common/datahooks/useInvitations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useAppInit from "common/hooks/useAppInit";
import BaseLayout from "common/layouts/BaseLayout";
import WorkspaceLayout from "common/layouts/WorkspaceLayout";

import CrashPage from "./common/components/CrashPage";
import useCustomer from "./common/datahooks/useCustomer";
import Invitation from "./pages/invitation/components/Invitation";
import Login from "./pages/login/Login";
import NoWorkspaces from "./pages/no-workspaces/components/NoWorkspaces";
import Questionnaire from "./pages/questionnaire/components/Questionnaire";
import { workspaceRoutes } from "./pages/routes";
import Workspaces from "./pages/workspaces/components/Workspaces";

function App() {
  useAppInit();
  const { isAuthorized } = useAuth();
  const { customerInfo } = useCustomer();
  const { workspaces } = useWorkspaces();
  const { invitations } = useInvitations();
  const [searchParams] = useSearchParams();

  const isAppLoaded =
    (getWhitelabelCustomization() && isAuthorized === false) ||
    (isAuthorized === true && customerInfo && workspaces && invitations);

  useEffect(() => {
    if (isAppLoaded) {
      document.getElementById("loader-container")?.remove();
    }
  }, [isAppLoaded]);

  if (!isAppLoaded) {
    return null;
  }

  let routes: JSX.Element;

  if (getWhitelabelCustomization()) {
    routes = isAuthorized ? (
      <>
        {workspaces.length ? (
          <Route path="/" element={<WorkspaceLayout />}>
            {workspaceRoutes.map(({ path, component }) => (
              <Route key={path} path={path} element={component} />
            ))}
            {/* since we don't have an index route atm, this redirects index route to first workspace route */}
            <Route
              path=""
              element={<Navigate to={workspaceRoutes[0].path} replace />}
            />
          </Route>
        ) : (
          <Route path="/no-workspaces" element={<BaseLayout />}>
            <Route path="" element={<NoWorkspaces />} />
          </Route>
        )}
        <Route
          path="*"
          element={
            <Navigate
              to={
                workspaces.length
                  ? `/${workspaceRoutes[0].path}?${searchParams.toString()}`
                  : "no-workspaces"
              }
              replace
            />
          }
        />
      </>
    ) : (
      <>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </>
    );
  } else {
    const inviteToken = searchParams.get("invite_token");
    const hasInvitation = !!(inviteToken || invitations.length);

    //by default, redirect to the first workspace
    let baseRoutesRedirectTo: string;
    let workspaceRoutesRedirectTo: string;

    if (hasInvitation) {
      baseRoutesRedirectTo = `/invitations/${inviteToken || invitations[0].token}`;
    } else if (customerInfo.show_questionnaire) {
      baseRoutesRedirectTo = "/questionnaire";
    } else if (!workspaces.length) {
      baseRoutesRedirectTo = "/no-workspaces";
      workspaceRoutesRedirectTo = "/no-workspaces";
    } else {
      const firstWorkspaceRedirectTo = `workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`;
      baseRoutesRedirectTo = firstWorkspaceRedirectTo;
      workspaceRoutesRedirectTo = firstWorkspaceRedirectTo;
    }

    routes = (
      <>
        {/* Base Routes */}
        <Route element={<BaseLayout />}>
          <Route path="invitations/:inviteToken" element={<Invitation />} />
          {customerInfo.show_questionnaire && (
            <Route path="questionnaire" element={<Questionnaire />} />
          )}
          {workspaces.length ? (
            <Route path="workspaces" element={<Workspaces />} />
          ) : (
            <Route path="no-workspaces" element={<NoWorkspaces />} />
          )}
          <Route
            path="*"
            element={<Navigate to={baseRoutesRedirectTo} replace />}
          />
        </Route>
        {/* Workspace routes */}
        {!customerInfo.show_questionnaire &&
          !hasInvitation &&
          workspaces.length && (
            <>
              <Route
                path="workspaces/:workspaceId"
                element={<WorkspaceLayout />}
              >
                {workspaceRoutes.map(({ path, component }) => (
                  <Route key={path} path={path} element={component} />
                ))}
                {/* since we don't have an index route atm, this redirects index route to first workspace route */}
                {/* e.g.: xxx/workspaces/1aa4c82b-cdf8-417e-bcda-966d661f981a/ */}
                <Route
                  path=""
                  element={<Navigate to={workspaceRoutes[0].path} replace />}
                />
              </Route>
              <Route path="*" element={workspaceRoutesRedirectTo} />
            </>
          )}
      </>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError, eventId }) => (
        <CrashPage resetError={resetError} eventId={eventId} />
      )}
    >
      <Routes>{routes}</Routes>
    </Sentry.ErrorBoundary>
  );
}

export default App;
